import { useEffect, useMemo, useRef, useState } from "react";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { TabGroup, Tab } from "../layout/TabGroup";
import {
  usePaymentRequestMessages,
  useMessageContent,
  openMessageAttachment,
} from "@api";
import { formatDateTime } from "@utils/Formatters";

function DetailRow({ label, value, bold = false }) {
  if (!value) return null;
  return (
    <div className="flex items-center px-2 py-1 cursor-default">
      <span className="w-20 shrink-0 text-zinc-400">{label}:</span>
      <span
        className={
          bold
            ? "min-w-0 truncate font-bold text-zinc-800"
            : "min-w-0 truncate text-zinc-700"
        }
      >
        {value}
      </span>
    </div>
  );
}

// Sizes itself to the archived HTML so the surrounding modal body does the
// scrolling instead of the frame. allow-same-origin (without allow-scripts)
// lets us measure the document while still blocking script execution.
function AutoHeightFrame({ html }) {
  const frameRef = useRef(null);
  const observerRef = useRef(null);
  const [height, setHeight] = useState(400);

  const handleLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.documentElement) return;
    const update = () =>
      setHeight(
        Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0),
      );
    update();
    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(doc.documentElement);
  };

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <iframe
      ref={frameRef}
      title="Message content"
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={handleLoad}
      scrolling="no"
      style={{ height }}
      className="w-full"
    />
  );
}

function closestTo(messages, targetDate) {
  if (!messages.length) return null;
  if (!targetDate) return messages[0];
  const target = new Date(targetDate).getTime();
  return messages
    .slice()
    .sort(
      (a, b) =>
        Math.abs(new Date(a.sentOn).getTime() - target) -
        Math.abs(new Date(b.sentOn).getTime() - target),
    )[0];
}

export default function MessageContentModal({
  isOpen,
  onClose,
  messageId = null,
  paymentRequestId = null,
  targetDate,
  elevated = false,
}) {
  const [selectedTab, setSelectedTab] = useState(null);

  const { data: allMessages, isLoading: messagesLoading } = usePaymentRequestMessages(
    paymentRequestId,
    isOpen && !messageId,
  );

  const messages = useMemo(
    () => (messageId ? [] : (allMessages ?? []).filter((m) => m.hasArchive)),
    [allMessages, messageId],
  );

  useEffect(() => {
    setSelectedTab(null);
  }, [messageId, paymentRequestId, targetDate]);

  const emailMessage = useMemo(
    () =>
      closestTo(
        messages.filter((m) => m.communicationMethod !== "Sms"),
        targetDate,
      ),
    [messages, targetDate],
  );
  const smsMessage = useMemo(
    () =>
      closestTo(
        messages.filter((m) => m.communicationMethod === "Sms"),
        targetDate,
      ),
    [messages, targetDate],
  );

  const activeTab = selectedTab ?? (emailMessage ? "email" : "sms");
  const activeId =
    messageId ?? (activeTab === "sms" ? smsMessage?.id : emailMessage?.id);

  const {
    data: content,
    isLoading: contentLoading,
    error,
  } = useMessageContent(activeId, isOpen);

  const noArchive = error?.response?.status === 404;
  const loading = (!messageId && messagesLoading) || (!!activeId && contentLoading);
  const isSms = content?.communicationMethod === "Sms";
  const showTabs = !messageId && !!emailMessage && !!smsMessage;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md-lg"
      title={isSms && !showTabs ? "SMS message" : "Sent message"}
      bodyClassName="flex flex-col overflow-y-auto bg-white"
      elevated={elevated}
    >
      {showTabs && (
        <div className="z-0 flex flex-col items-center pt-3">
          <TabGroup
            className="my-0 pointer-events-auto shadow-sm rounded-full"
            variant="pill"
          >
            <Tab
              active={activeTab === "email"}
              onClick={() => setSelectedTab("email")}
              variant="pill"
            >
              Email
            </Tab>
            <Tab
              active={activeTab === "sms"}
              onClick={() => setSelectedTab("sms")}
              variant="pill"
            >
              SMS
            </Tab>
          </TabGroup>
        </div>
      )}

      {loading && <div className="py-10 text-center text-sm text-zinc-500">Loading…</div>}

      {!loading && (noArchive || (!messageId && !messagesLoading && !messages.length)) && (
        <div className="py-10 text-center text-sm text-zinc-500">
          No archived copy of this message is available.
        </div>
      )}

      {!loading && content && !isSms && (
        <div className="w-full flex flex-col items-center pb-8 pt-6 px-6">
          <div
            className="pt-6 rounded-3xl flex flex-col items-stretch w-full bg-white overflow-hidden"
            style={{ boxShadow: "4px 4px 16px 0px rgba(0,0,0,0.16)" }}
          >
            <div className="px-6 border-b border-zinc-200 py-4">
              <div className="text-xs space-y-1 pb-2 px-2">
                <DetailRow label="From" value={content.from} />
                <DetailRow label="To" value={content.to} />
                <DetailRow label="Cc" value={content.cc} />
                <DetailRow label="Subject" value={content.subject} bold />
                <DetailRow label="Sent" value={formatDateTime(content.sentAt)} />
              </div>
              {content.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2 mx-2">
                  {content.attachments.map((attachment) => (
                    <button
                      key={attachment.index}
                      type="button"
                      onClick={() => openMessageAttachment(content.id, attachment.index)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-50 rounded-md border border-zinc-200 hover:border-zinc-400"
                    >
                      <PaperClipIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                      <span className="text-sm text-zinc-500">
                        {attachment.fileName}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {content.htmlBody ? (
              <AutoHeightFrame html={content.htmlBody} />
            ) : (
              <div className="px-8 py-6 whitespace-pre-wrap text-sm text-zinc-800">
                {content.textBody}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && content && isSms && (
        <div className="w-full flex flex-col items-center pb-8 pt-6 px-6">
          <div
            className="pt-6 rounded-3xl flex flex-col items-stretch w-full bg-white overflow-hidden"
            style={{ boxShadow: "4px 4px 16px 0px rgba(0,0,0,0.16)" }}
          >
            <div className="px-6 border-b border-zinc-200 py-4">
              <div className="text-xs space-y-1 pb-2 px-2">
                <DetailRow label="From" value={content.from} />
                <DetailRow label="To" value={content.to} />
                <DetailRow label="Sent" value={formatDateTime(content.sentAt)} />
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="w-full max-w-xl">
                <div className="bg-zinc-100 rounded-3xl max-w-[70%] p-4">
                  <p className="text-sm whitespace-pre-wrap break-words cursor-default">
                    {content.textBody}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
